import { type Address, type AddressCoords } from '@/lib/types';

export function getAutocompleteResultsForAddress(
  address: string,
): Promise<
  | google.maps.places.AutocompletePrediction[]
  | google.maps.places.PlacesServiceStatus
> {
  const autocompleteService =
    new window.google.maps.places.AutocompleteService();
  const OK = window.google.maps.places.PlacesServiceStatus.OK;
  const ZERO_RESULTS =
    window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS;

  return new Promise((resolve, reject) => {
    autocompleteService.getPlacePredictions(
      {
        input: address,
        componentRestrictions: { country: 'au' },
      },
      (results, status) => {
        if ([OK, ZERO_RESULTS].includes(status)) {
          resolve(results || []);
        }
        reject(status);
      },
    );
  });
}

export function geocodeByAddress(
  address: string,
): Promise<google.maps.GeocoderResult[] | google.maps.GeocoderStatus> {
  const geocoder = new window.google.maps.Geocoder();
  const OK = window.google.maps.GeocoderStatus.OK;

  return new Promise((resolve, reject) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status === OK) {
        resolve(results || []);
      }
      reject(status);
    });
  });
}

export function geocoderResultToAddressCoords(
  result: google.maps.GeocoderResult,
): Promise<AddressCoords> {
  return new Promise((resolve, reject) => {
    try {
      const latLng = {
        lat: result.geometry.location.lat(),
        long: result.geometry.location.lng(),
      };
      resolve(latLng);
    } catch (error) {
      reject(error);
    }
  });
}

export function geocoderResultToAddress(
  result: google.maps.GeocoderResult,
): Address {
  const address = {
    unitNumber: '',
    addressLine: '',
    city: '',
    state: '',
    country: '',
    postCode: '',
  };

  for (const component of result.address_components || []) {
    if (component.types.includes(GoogleAddressType.subpremise)) {
      address.addressLine = component.long_name + '/';
    } else if (component.types.includes(GoogleAddressType.street_number)) {
      address.addressLine += component.long_name;
    } else if (component.types.includes(GoogleAddressType.route)) {
      address.addressLine += ' ' + component.long_name;
    } else if (component.types.includes(GoogleAddressType.locality)) {
      address.city = component.long_name;
    } else if (
      component.types.includes(GoogleAddressType.administrative_area_level_1)
    ) {
      address.state = component.long_name;
    } else if (component.types.includes(GoogleAddressType.country)) {
      address.country = component.long_name;
    } else if (component.types.includes(GoogleAddressType.postal_code)) {
      address.postCode = component.long_name;
    }
  }

  return address;
}

export enum GoogleAddressType {
  administrative_area_level_1 = 'administrative_area_level_1',
  administrative_area_level_2 = 'administrative_area_level_2',
  administrative_area_level_3 = 'administrative_area_level_3',
  administrative_area_level_4 = 'administrative_area_level_4',
  administrative_area_level_5 = 'administrative_area_level_5',
  administrative_area_level_6 = 'administrative_area_level_6',
  administrative_area_level_7 = 'administrative_area_level_7',
  archipelago = 'archipelago',
  colloquial_area = 'colloquial_area',
  continent = 'continent',
  country = 'country',
  establishment = 'establishment',
  finance = 'finance',
  floor = 'floor',
  food = 'food',
  general_contractor = 'general_contractor',
  geocode = 'geocode',
  health = 'health',
  intersection = 'intersection',
  landmark = 'landmark',
  locality = 'locality',
  natural_feature = 'natural_feature',
  neighborhood = 'neighborhood',
  place_of_worship = 'place_of_worship',
  plus_code = 'plus_code',
  point_of_interest = 'point_of_interest',
  political = 'political',
  post_box = 'post_box',
  postal_code = 'postal_code',
  postal_code_prefix = 'postal_code_prefix',
  postal_code_suffix = 'postal_code_suffix',
  postal_town = 'postal_town',
  premise = 'premise',
  room = 'room',
  route = 'route',
  street_address = 'street_address',
  street_number = 'street_number',
  sublocality = 'sublocality',
  sublocality_level_1 = 'sublocality_level_1',
  sublocality_level_2 = 'sublocality_level_2',
  sublocality_level_3 = 'sublocality_level_3',
  sublocality_level_4 = 'sublocality_level_4',
  sublocality_level_5 = 'sublocality_level_5',
  subpremise = 'subpremise',
  town_square = 'town_square',
}
